import api from '../utils/axiosConfig';

/**
 * Promote a student to the next round
 * 
 * @param {number} studentId - Student ID
 * @param {number} companyId - Company ID
 * @param {number} currentRoundNumber - Current round number
 * @returns {Promise<Object>} - Promotion result
 */
export const promoteToHigherRound = async (studentId, companyId, currentRoundNumber) => {
  try {
    const response = await api.post('/scheduling/promote-to-higher-round/', {
      student_id: studentId,
      company_id: companyId,
      current_round_number: currentRoundNumber
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Get higher round candidates for a specific company and round
 * 
 * @param {number} companyId - Company ID
 * @param {number} roundNumber - Round number
 * @returns {Promise<Object>} - Higher round candidates data
 */
export const getHigherRoundCandidates = async (companyId, roundNumber) => {
  try {
    const response = await api.get('/scheduling/get-higher-round-candidates/', {
      params: {
        company_id: companyId,
        round_number: roundNumber
      }
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Schedule a higher round candidate
 * 
 * @param {Object} data - Scheduling data
 * @returns {Promise<Object>} - Scheduled interview data
 */
export const scheduleHigherRoundCandidate = async (data) => {
  try {
    const response = await api.post('/scheduling/schedule-higher-round-candidate/', data);
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Mark an interview as done or toggle its completion status
 * 
 * @param {number} interviewId - Interview ID
 * @returns {Promise<Object>} - Updated interview data
 */
export const markInterviewDone = async (interviewId) => {
  try {
    const response = await api.post('/scheduling/mark-interview-done/', {
      interview_id: interviewId
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Get the interview schedule for a specific date and company
 * 
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {number} companyId - Optional company ID to filter by
 * @returns {Promise<Object>} - Interview schedule data
 */
export const getInterviewSchedule = async (date, companyId = null) => {
  try {
    let url = `/scheduling/get-interview-schedule/?date=${date}&include_time_slots=true`;
    
    if (companyId) {
      url += `&company_id=${companyId}`;
    }
    
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Schedule interviews for a specific date
 * 
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<Object>} - Scheduled interviews data
 */
export const scheduleInterviews = async (date) => {
  try {
    const response = await api.post('/scheduling/schedule-interviews/', {
      date: date
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Update the status of an interview
 * 
 * @param {number} interviewId - ID of the interview to update
 * @param {string} status - New status (SCHEDULED, COMPLETED, ABSENT, CANCELLED)
 * @returns {Promise<Object>} - Updated interview data
 */
export const updateInterviewStatus = async (interviewId, status) => {
  try {
    const response = await api.patch(`/scheduling/update-interview-status/${interviewId}/`, {
      status: status
    });
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Get the global interview schedule for multiple companies on a specific date
 * 
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array} companyIds - Array of company IDs to include in the global view
 * @returns {Promise<Object>} - Global schedule data including interviews and conflicts
 */
export const getGlobalSchedule = async (date, companyIds = []) => {
  try {
    let url = `/scheduling/get-global-schedule/?date=${date}`;
    
    if (companyIds && companyIds.length > 0) {
      url += `&company_ids=${companyIds.join(',')}`;
    }
    
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Resolve a scheduling conflict by moving one of the conflicting interviews
 * 
 * @param {number} interviewId - ID of the interview to move
 * @param {Object} newSlot - New time slot and panel data for the interview
 * @returns {Promise<Object>} - Updated interview data
 */
export const resolveConflict = async (interviewId, newSlot) => {
  try {
    const response = await api.patch(`/scheduling/resolve-conflict/${interviewId}/`, newSlot);
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Bulk reschedule interviews based on conflict resolution
 * 
 * @param {Array} changes - Array of interview changes (each with interviewId and new slot)
 * @returns {Promise<Object>} - Result of the bulk update
 */
export const bulkRescheduleInterviews = async (changes) => {
  try {
    const response = await api.post('/scheduling/bulk-reschedule/', { changes });
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};


/**
 * Move an interview to a different time slot or panel
 * 
 * @param {number} interviewId - ID of the interview to move
 * @param {Object} newSlotData - New time slot and panel data
 * @returns {Promise} - Updated interview data
 */
export const moveInterview = async (interviewId, newSlotData) => {
  try {
    const response = await api.patch(`/scheduling/move-interview/${interviewId}/`, newSlotData);
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Manually assign a student to an interview slot
 * 
 * @param {Object} interviewData - Data for the interview assignment
 * @returns {Promise} - Created interview data
 */
export const assignInterview = async (interviewData) => {
  try {
    const response = await api.post('/scheduling/assign-interview/', interviewData);
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Check if a student has conflicting interviews at a specific time
 * 
 * @param {number} studentId - ID of the student
 * @param {string} startTime - Start time (HH:MM)
 * @param {string} endTime - End time (HH:MM)
 * @param {string} date - Date (YYYY-MM-DD)
 * @param {number} excludeInterviewId - Optional interview ID to exclude from check
 * @returns {Promise} - Conflict data if any
 */
export const checkStudentConflicts = async (studentId, startTime, endTime, date, excludeInterviewId = null) => {
  try {
    const params = {
      student_id: studentId,
      start_time: startTime,
      end_time: endTime,
      date: date
    };
    
    if (excludeInterviewId) {
      params.exclude_interview_id = excludeInterviewId;
    }
    
    const response = await api.get('/scheduling/check-student-conflicts/', { params });
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Force reschedule an interview even if there are conflicts
 * 
 * @param {Object} interviewData - Data for the interview assignment
 * @returns {Promise} - Created interview data
 */
export const forceReschedule = async (interviewData) => {
  try {
    const response = await api.post('/scheduling/force-reschedule/', interviewData);
    return response.data;
  } catch (error) {
    throw error?.response?.data || error;
  }
};

/**
 * Custom scheduling via Excel upload
 * 
 * @param {FormData} formData - Form data with company_id, round_number, date_str, excel_file, and force_schedule
 * @returns {Promise} - Scheduling result data
 */
export const customScheduling = async (formData) => {
  try {
    const response = await api.post('/scheduling/custom-scheduling/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * GD scheduling
 * 
 * @param {Object} gdData - GD scheduling data with company_id, date_str, times, and student_emails
 * @returns {Promise} - Scheduling result data
 */
export const gdScheduling = async (gdData) => {
  try {
    const response = await api.post('/scheduling/gd-scheduling/', gdData);
    return response;
  } catch (error) {
    throw error;
  }
};

export const schedulingApi = {
  getInterviewSchedule,
  scheduleInterviews,
  updateInterviewStatus,
  getGlobalSchedule,
  resolveConflict,
  bulkRescheduleInterviews,
  moveInterview,
  assignInterview,
  checkStudentConflicts,
  forceReschedule,
  customScheduling,
  gdScheduling
};